import { inject, Injectable } from '@angular/core';

import { ColumnDataType } from '@blaze-shared/api-contracts-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { ColumnType, DatasetColumn, isLinkedColumn } from '@blaze-shared/schema-domain';

import { ContextValue } from './models/intelli-autocomplete.models';
import { AutocompleteConfig, IntelliConfig, ParentFormula } from './models/intellisense';
import {
  SUPPORTED_ARRAY_TYPES,
  SUPPORTED_COLUMN_TYPES,
  SUPPORTED_OPERATOR_COLUMN_TYPES_MULTIPLE,
  SUPPORTED_OPERATOR_COLUMN_TYPES_SINGLE,
} from './models/intellisense-configuration';

@Injectable({ providedIn: 'root' })
export class IntelliValueFiltersService {
  private readonly schemaService = inject(SchemaService);

  filterOutDuplicateValues(ctxValues: ContextValue[]): ContextValue[] {
    const constantValues = new Set<ContextValue>();
    const filteredValues: ContextValue[] = [];
    const parsedValuesSet = new Set<string>();

    // Filter out values by their literal JSON structure
    ctxValues.forEach((ctxValue) => {
      const value = JSON.stringify(ctxValue);
      if (!parsedValuesSet.has(value) && ctxValue.type !== 'Constant') {
        filteredValues.push(ctxValue);
        parsedValuesSet.add(value);
      } else if (ctxValue.type === 'Constant') {
        // Keep constants separate for further proce
        constantValues.add(ctxValue);
      }
    });

    // Filter out constants such as tags which can have a different column name even though the actual value stays
    // the same. In such a case, keep the first occurence of a constant.
    constantValues.forEach((constantValue) => {
      if (!parsedValuesSet.has(constantValue.value)) {
        filteredValues.push(constantValue);
        parsedValuesSet.add(constantValue.value);
      }
    });

    return filteredValues;
  }

  /**
   * Checks the passed value and accepts it only if it should be displayed in the autocomplete given the config column,
   * parent formula, etc.
   */
  filterOutUnsupportedDataType(
    value: ContextValue,
    config: IntelliConfig | AutocompleteConfig | null,
    parentFormula?: ParentFormula,
  ): boolean {
    const supportedTypes = this.getSupportedDataTypes(value, config, parentFormula);
    // If there are restrictions, always allow Array/Object for drill-down navigation.
    const dataTypes = supportedTypes.length
      ? [...new Set([...supportedTypes, 'Array', 'Object'])].map((t) => t?.toLowerCase())
      : [];

    // No restrictions found - do not filter out the value.
    if (!dataTypes?.length) {
      return true;
    }

    if (value.isArray && dataTypes.includes('array')) {
      return true;
    }

    // Check if the value data type matches any of the allowed types. We also check columnType in case it's a link
    // and a link is an allowed value (links have columnType = link but the data type is the type of the linked value)
    if (typeof value.valueDataType === 'string' && !!dataTypes?.length) {
      const dataType = value.valueDataType?.toLowerCase();
      const columnType = (value.columnType || '')?.toLowerCase();
      return dataTypes.includes(dataType) || dataTypes.includes(columnType);
    }
    return true;
  }

  filterOutValueByEnteredText(value: ContextValue, text: string): boolean {
    text = `${text}`?.toLowerCase();
    const vDisplay = this.schemaService.replaceColumnAndTagIds(value?.display ?? '');
    const vValue = this.schemaService.replaceColumnAndTagIds(value?.value ?? '');
    return `${vDisplay}`.toLowerCase().includes(text) || `${vValue}`.toLowerCase().includes(text);
  }

  getColumnDataType(column: DatasetColumn): ColumnDataType | undefined {
    return isLinkedColumn(column) ? column.link?.dataType : column?.dataType;
  }

  /**
   * Returns a list of datatypes supported by the current configuration and formula. For example, if a passed config
   * column is a type, it'll return only the types supported by that column type. If the column accepts arrays,
   * it'll only return types appropriate for the array column.
   * @returns
   */
  getSupportedDataTypes(
    value: ContextValue,
    config: IntelliConfig | AutocompleteConfig | null,
    parentFormula?: ParentFormula,
  ): ColumnType[] {
    // If we're inside a formula, datatypes of the currently typed parameter takes precedence.
    if (parentFormula?.parameter?.dataTypes) {
      return parentFormula.parameter.dataTypes;
    }

    let dataTypes: ColumnType[] = [];
    const operator = config?.operator;
    const selectedColumnDataType = (config?.column?.columnType || '') as ColumnType;
    const selectedColumnIsArray =
      config?.column?.dataType === 'Array' ||
      config?.column?.columnType === 'Array' ||
      config?.column?.multipleValues === true;
    const selectedColumnIsMultiValue =
      config?.column?.dataType !== 'Array' &&
      config?.column?.columnType !== 'Array' &&
      config?.column?.multipleValues === true;
    const selectedDataTypes = config?.columnTypes || [];

    if (selectedColumnDataType && !selectedDataTypes.find((dt) => dt === selectedColumnDataType)) {
      selectedDataTypes.push(selectedColumnDataType);
    }

    if (selectedDataTypes.length && !operator) {
      if (selectedColumnIsArray && !selectedColumnIsMultiValue) {
        // There is no operator so the only values an array column can support are also arrays. For example, an insert
        // node assignment can only assign arrays to the insert field. However, if an would be operator is present,
        // this could be intellisense instance used in filters and therefore a filter ArrayCol contains StringCol
        // would make sense.
        dataTypes = [...SUPPORTED_ARRAY_TYPES];
      } else {
        // No operator specified and it's not an array. We only care about the types supported by the given column.
        for (const dataType of selectedDataTypes) {
          dataTypes.push(...SUPPORTED_COLUMN_TYPES[dataType]);
        }
        if (selectedColumnIsMultiValue) {
          dataTypes.push(...SUPPORTED_ARRAY_TYPES);
        }
      }

      return dataTypes;
    } else if (selectedDataTypes.length && operator) {
      let SUPPORTED_TYPES = SUPPORTED_OPERATOR_COLUMN_TYPES_SINGLE;
      // Specified operator is Array-specific. Overwrite dataTypes to support only Array suitable types.
      if (value?.isArray || operator === 'In' || operator === 'NotIn') {
        SUPPORTED_TYPES = SUPPORTED_OPERATOR_COLUMN_TYPES_MULTIPLE;
        dataTypes = [...SUPPORTED_ARRAY_TYPES];
      }

      for (const dataType of selectedDataTypes) {
        dataTypes.push(...SUPPORTED_COLUMN_TYPES[dataType]);
      }

      if (selectedColumnIsMultiValue) {
        dataTypes.push(...SUPPORTED_ARRAY_TYPES);
      }

      const columnTypes = new Set(dataTypes);
      const operatorTypes = new Set(SUPPORTED_TYPES[operator]);
      const intersection = [...columnTypes].filter((t) => operatorTypes.has(t));
      return intersection;
    }

    return dataTypes;
  }
}
